import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import Head from 'next/head';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
// import CategoryContent from '@/components/Category';
import { getArticleListData, getCategoryTopList, getThemeData } from '@/lib/service';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import CategoryContent from '@/components/Category';
import { getTdkApi } from '@/lib/service/tdk';
import { blogListJsonLd } from '@/lib/common/jsonld';
import { hyphenatedToTitleCase } from '@/shared/path-helper';
import React from 'react';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';

// ⭐ ISR: 转换为静态生成 + 1 小时重新验证
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale = 'en' } = context;
  const resolvedUrl = '/blog';
  const pageSize = 10;

  const [articleListRes, bookListRes, localeRes, themeData, tdkData] = await Promise.all([
    getArticleListData({ page_no: 1, page_size: pageSize }, locale), // 列表
    getCategoryTopList({}, locale), // 获取置顶文章
    serverSideTranslations(locale, ['common']),
    getThemeData(locale),
    getTdkApi('/blog', locale),
  ]);
  let currentURL = `${process.env.NEXT_APP_WEB_HOST}${resolvedUrl}`;
  if (locale != 'en') {
    currentURL = `${process.env.NEXT_APP_WEB_HOST}/${locale}${resolvedUrl}`;
  }
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `${process.env.NEXT_APP_WEB_HOST}/${locale}` : `${process.env.NEXT_APP_WEB_HOST}`;

  const pageTitle = tdkData?.data?.title ?? tempData['head_Meta_blog_Title'];

  const pageDescription = tdkData?.data?.description ?? tempData['head_Meta_Desc'];
  const jsonLd = blogListJsonLd(
    {
      title: hyphenatedToTitleCase(resolvedUrl),
      desc: pageDescription,
      url: currentURL,
    },
    {
      url: siteUrl,
    },
  );

  // ✅ 在服务端判断是否显示 CategoryTopContent，避免客户端条件渲染导致 CLS
  const bookListData = bookListRes?.data || [];
  const showTopContent = bookListData.length > 0 && bookListData.every((v) => v.cover_image);

  return {
    props: {
      ...localeRes,
      articleList: articleListRes || {},
      bookList: bookListData,
      classifies: themeData.data.classifies || [],
      herf: currentURL,
      locale,
      title: pageTitle,
      description: pageDescription,
      jsonLd: jsonLd,
      pageNo: 1,
      pageSize: pageSize,
      totalSize: articleListRes?.total || 0,
      type: '',
      showTopContent, // ✅ 新增：服务端判断结果
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
    },
    revalidate: ISR_REVALIDATE_TIME,
  };
};

const CategoryPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  return (
    <>
      <ISRDebugPanel
        generatedAt={props.isrGeneratedAt}
        revalidateIn={props.isrRevalidateIn}
        pageName="Blog"
        additionalInfo={{
          语言: props.locale,
        }}
      />
      <CategoryContent data={props} />
    </>
  );
};

CategoryPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const jsonLd = page.props?.jsonLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="alternate" hrefLang="en" href={`${process.env.NEXT_APP_WEB_HOST}/en/blog`} />
          <link rel="alternate" hrefLang="tw" href={`${process.env.NEXT_APP_WEB_HOST}/tw/blog`} />
          <link rel="alternate" hrefLang="zh" href={`${process.env.NEXT_APP_WEB_HOST}/zh/blog`} />
          <link rel="alternate" hrefLang="ja" href={`${process.env.NEXT_APP_WEB_HOST}/ja/blog`} />
          <link rel="alternate" hrefLang="ko" href={`${process.env.NEXT_APP_WEB_HOST}/ko/blog`} />
          <link rel="alternate" hrefLang="pt" href={`${process.env.NEXT_APP_WEB_HOST}/pt/blog`} />
          <link rel="alternate" hrefLang="de" href={`${process.env.NEXT_APP_WEB_HOST}/de/blog`} />
          <link rel="alternate" hrefLang="es" href={`${process.env.NEXT_APP_WEB_HOST}/es/blog`} />
          <link rel="alternate" hrefLang="it" href={`${process.env.NEXT_APP_WEB_HOST}/it/blog`} />
          <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_APP_WEB_HOST}/blog`} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default CategoryPage;
